import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { PAYMENT_STATUSES, StatusBadge, formatCurrency, formatDate } from '@/lib/constants';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Card, CardContent } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Plus, Search, Pencil, Trash2, Filter, X, TrendingUp, AlertTriangle, Clock, List, CalendarDays } from 'lucide-react';
import { toast } from 'sonner';
import CalendarView from '@/components/CalendarView';

const INITIAL_FORM = {
  client_name: '', project_name: '', invoice_ref: '', amount_ht: 0, amount_ttc: 0,
  due_date: '', status: 'a_venir', payment_method: '', followup_done: false, comments: '',
};

export default function PaymentsPage() {
  const [payments, setPayments] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPayment, setEditingPayment] = useState(null);
  const [form, setForm] = useState(INITIAL_FORM);
  const [filters, setFilters] = useState({ status: '', search: '' });
  const [viewMode, setViewMode] = useState('table');

  const fetchData = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.search) params.append('search', filters.search);
      const [paymentsRes, statsRes] = await Promise.all([
        api.get(`/payments?${params}`),
        api.get('/payments/stats'),
      ]);
      setPayments(paymentsRes.data);
      setStats(statsRes.data);
    } catch { toast.error('Erreur lors du chargement'); }
    finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSave = async () => {
    try {
      if (editingPayment) {
        await api.put(`/payments/${editingPayment.id}`, form);
        toast.success('Paiement mis a jour');
      } else {
        await api.post('/payments', form);
        toast.success('Paiement cree');
      }
      setDialogOpen(false);
      setEditingPayment(null);
      setForm(INITIAL_FORM);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const handleEdit = (payment) => {
    setEditingPayment(payment);
    setForm({
      client_name: payment.client_name || '',
      project_name: payment.project_name || '',
      invoice_ref: payment.invoice_ref || '',
      amount_ht: payment.amount_ht || 0,
      amount_ttc: payment.amount_ttc || 0,
      due_date: payment.due_date ? payment.due_date.split('T')[0] : '',
      status: payment.status || 'a_venir',
      payment_method: payment.payment_method || '',
      followup_done: payment.followup_done || false,
      comments: payment.comments || '',
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce paiement ?')) return;
    try { await api.delete(`/payments/${id}`); toast.success('Paiement supprime'); fetchData(); }
    catch { toast.error('Erreur'); }
  };

  const handleNew = () => { setEditingPayment(null); setForm(INITIAL_FORM); setDialogOpen(true); };

  return (
    <div className="space-y-6" data-testid="payments-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-zinc-50 tracking-tight" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Paiements</h1>
          <p className="text-sm text-zinc-500 mt-1">Suivi des règlements clients</p>
        </div>
        <Button data-testid="new-payment-btn" onClick={handleNew} className="bg-amber-500 text-zinc-950 hover:bg-amber-400 rounded-sm font-medium">
          <Plus className="w-4 h-4 mr-2" /> Nouveau paiement
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-zinc-900 border-zinc-800 rounded-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-sky-500/10 rounded-sm flex items-center justify-center"><Clock className="w-4 h-4 text-sky-400" /></div>
              <div>
                <p className="text-xs text-zinc-500 uppercase tracking-wider">À recevoir</p>
                <p className="text-lg font-bold text-sky-400" style={{fontFamily:'Cabinet Grotesk'}}>{formatCurrency(stats?.total_due)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-zinc-900 border-zinc-800 rounded-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-emerald-500/10 rounded-sm flex items-center justify-center"><TrendingUp className="w-4 h-4 text-emerald-400" /></div>
              <div>
                <p className="text-xs text-zinc-500 uppercase tracking-wider">Encaissé</p>
                <p className="text-lg font-bold text-emerald-400" style={{fontFamily:'Cabinet Grotesk'}}>{formatCurrency(stats?.total_received)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-zinc-900 border-zinc-800 rounded-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-rose-500/10 rounded-sm flex items-center justify-center"><AlertTriangle className="w-4 h-4 text-rose-400" /></div>
              <div>
                <p className="text-xs text-zinc-500 uppercase tracking-wider">En retard</p>
                <p className="text-lg font-bold text-rose-400" style={{fontFamily:'Cabinet Grotesk'}}>{formatCurrency(stats?.total_late)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-zinc-900 border-zinc-800 rounded-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-amber-500/10 rounded-sm flex items-center justify-center">
                <span className="text-sm font-bold text-amber-500">{stats?.count_late || 0}</span>
              </div>
              <div>
                <p className="text-xs text-zinc-500 uppercase tracking-wider">Retards</p>
                <p className="text-sm text-zinc-400">{stats?.count_due || 0} en attente</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* TVA Summary */}
      {stats && (
        <Card className="bg-zinc-900 border-zinc-800 rounded-sm">
          <CardContent className="p-4">
            <p className="text-xs text-zinc-500 uppercase tracking-wider mb-3">Estimation TVA (20%)</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Encaissé */}
              <div className="bg-zinc-950/50 border border-zinc-800 rounded-sm p-3">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-2 h-2 rounded-full bg-emerald-400" />
                  <span className="text-xs font-medium text-emerald-400 uppercase tracking-wider">TVA encaissée</span>
                </div>
                <div className="flex items-center gap-4">
                  <div>
                    <p className="text-[10px] text-zinc-600">TTC</p>
                    <p className="text-sm font-semibold text-zinc-200" style={{fontFamily:'Cabinet Grotesk'}}>{formatCurrency(stats.total_received)}</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-zinc-600">HT</p>
                    <p className="text-sm font-semibold text-zinc-400" style={{fontFamily:'Cabinet Grotesk'}}>{formatCurrency(stats.vat_received_ht || 0)}</p>
                  </div>
                  <div className="ml-auto text-right">
                    <p className="text-[10px] text-zinc-600">À reverser</p>
                    <p className="text-base font-bold text-amber-400" style={{fontFamily:'Cabinet Grotesk'}} data-testid="tva-received">{formatCurrency(stats.vat_received_amount || 0)}</p>
                  </div>
                </div>
              </div>
              {/* Prévisionnel */}
              <div className="bg-zinc-950/50 border border-zinc-800 rounded-sm p-3">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-2 h-2 rounded-full bg-sky-400" />
                  <span className="text-xs font-medium text-sky-400 uppercase tracking-wider">TVA prévisionnelle (à venir + en retard)</span>
                </div>
                <div className="flex items-center gap-4">
                  <div>
                    <p className="text-[10px] text-zinc-600">TTC</p>
                    <p className="text-sm font-semibold text-zinc-200" style={{fontFamily:'Cabinet Grotesk'}}>{formatCurrency(stats.vat_forecast_ttc || 0)}</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-zinc-600">HT</p>
                    <p className="text-sm font-semibold text-zinc-400" style={{fontFamily:'Cabinet Grotesk'}}>{formatCurrency(stats.vat_forecast_ht || 0)}</p>
                  </div>
                  <div className="ml-auto text-right">
                    <p className="text-[10px] text-zinc-600">À prévoir</p>
                    <p className="text-base font-bold text-sky-400" style={{fontFamily:'Cabinet Grotesk'}} data-testid="tva-forecast">{formatCurrency(stats.vat_forecast_amount || 0)}</p>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Monthly VAT forecast (21 → 20 cycle) */}
      {stats?.vat_monthly?.length > 0 && (
        <Card className="bg-zinc-900 border-zinc-800 rounded-sm" data-testid="vat-monthly-card">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs text-zinc-500 uppercase tracking-wider">TVA prévisionnelle par mois (cycle 21 → 20)</p>
              <span className="text-[10px] text-zinc-600">Une période = du 21 du mois précédent au 20 du mois courant</span>
            </div>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-zinc-800 hover:bg-transparent">
                    <TableHead className="text-xs uppercase tracking-wider text-zinc-500 font-medium">Période</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-zinc-500 font-medium text-right">TTC encaissé</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-zinc-500 font-medium text-right">TVA encaissée</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-zinc-500 font-medium text-right">TTC prévisionnel</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-zinc-500 font-medium text-right">TVA prévisionnelle</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-zinc-500 font-medium text-right">TVA totale du mois</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {stats.vat_monthly.map(b => {
                    const totalVat = (b.received_vat || 0) + (b.forecast_vat || 0);
                    return (
                      <TableRow key={b.period} className="border-zinc-800/50 nexus-table-row" data-testid={`vat-month-${b.period}`}>
                        <TableCell className="text-sm">
                          <p className="text-zinc-100 font-medium capitalize">{b.label}</p>
                          <p className="text-[10px] text-zinc-600 font-mono">{b.start_date} → {b.end_date}</p>
                        </TableCell>
                        <TableCell className="text-sm text-zinc-300 text-right">{formatCurrency(b.received_ttc)}</TableCell>
                        <TableCell className="text-sm text-emerald-400 text-right font-medium">{formatCurrency(b.received_vat)}</TableCell>
                        <TableCell className="text-sm text-zinc-400 text-right">{formatCurrency(b.forecast_ttc)}</TableCell>
                        <TableCell className="text-sm text-sky-400 text-right font-medium">{formatCurrency(b.forecast_vat)}</TableCell>
                        <TableCell className="text-sm text-amber-400 text-right font-bold" style={{fontFamily:'Cabinet Grotesk'}}>{formatCurrency(totalVat)}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 bg-zinc-900 border border-zinc-800 rounded-sm p-4">
        <Filter className="w-4 h-4 text-zinc-500" />
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
          <Input data-testid="payment-search" placeholder="Rechercher..." value={filters.search}
            onChange={(e) => setFilters(f => ({ ...f, search: e.target.value }))}
            className="pl-9 w-48 bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm h-9 text-sm" />
        </div>
        <Select value={filters.status} onValueChange={(v) => setFilters(f => ({ ...f, status: v === 'all' ? '' : v }))}>
          <SelectTrigger className="w-44 bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm h-9 text-sm">
            <SelectValue placeholder="Statut" />
          </SelectTrigger>
          <SelectContent className="bg-zinc-900 border-zinc-800">
            <SelectItem value="all">Tous</SelectItem>
            {Object.entries(PAYMENT_STATUSES).map(([k, v]) => (<SelectItem key={k} value={k}>{v.label}</SelectItem>))}
          </SelectContent>
        </Select>
        {(filters.status || filters.search) && (
          <Button variant="ghost" size="sm" onClick={() => setFilters({ status: '', search: '' })} className="text-zinc-400 hover:text-zinc-100 h-9">
            <X className="w-4 h-4 mr-1" /> Effacer
          </Button>
        )}
        <div className="ml-auto flex items-center gap-1 bg-zinc-950 border border-zinc-800 rounded-sm p-0.5">
          <button data-testid="payment-view-table" onClick={() => setViewMode('table')}
            className={`p-1.5 rounded-sm transition-colors ${viewMode === 'table' ? 'bg-zinc-800 text-amber-500' : 'text-zinc-500 hover:text-zinc-300'}`}>
            <List className="w-4 h-4" />
          </button>
          <button data-testid="payment-view-calendar" onClick={() => setViewMode('calendar')}
            className={`p-1.5 rounded-sm transition-colors ${viewMode === 'calendar' ? 'bg-zinc-800 text-amber-500' : 'text-zinc-500 hover:text-zinc-300'}`}>
            <CalendarDays className="w-4 h-4" />
          </button>
        </div>
      </div>

      {viewMode === 'calendar' ? (
        <CalendarView
          items={payments}
          dateField="due_date"
          onItemClick={(item) => handleEdit(item)}
          renderItem={(item) => (
            <span className={`${PAYMENT_STATUSES[item.status]?.color?.split(' ')[1] || 'text-zinc-400'}`}>
              {item.client_name} - {formatCurrency(item.amount_ttc)}
            </span>
          )}
        />
      ) : (
      /* Table */
      <div className="bg-zinc-900 border border-zinc-800 rounded-sm overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="border-zinc-800 hover:bg-transparent">
              <TableHead className="text-xs uppercase tracking-wider text-zinc-500 font-medium">Client</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-zinc-500 font-medium">Projet / Facture</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-zinc-500 font-medium">Montant TTC</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-zinc-500 font-medium">Echeance</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-zinc-500 font-medium">Statut</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-zinc-500 font-medium">Relance</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-zinc-500 font-medium text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              [...Array(5)].map((_, i) => (
                <TableRow key={i} className="border-zinc-800/50">
                  {[...Array(7)].map((__, j) => (<TableCell key={j}><div className="h-4 nexus-shimmer rounded w-20" /></TableCell>))}
                </TableRow>
              ))
            ) : payments.length === 0 ? (
              <TableRow><TableCell colSpan={7} className="text-center py-12 text-zinc-500">Aucun paiement enregistre</TableCell></TableRow>
            ) : payments.map((p) => (
              <TableRow key={p.id} className="border-zinc-800/50 nexus-table-row">
                <TableCell className="text-sm text-zinc-300">{p.client_name}</TableCell>
                <TableCell className="text-sm text-zinc-200">
                  {p.project_name}{p.invoice_ref && <span className="text-zinc-500 ml-1 font-mono text-xs">({p.invoice_ref})</span>}
                </TableCell>
                <TableCell className="text-sm text-zinc-300 font-medium">{formatCurrency(p.amount_ttc)}</TableCell>
                <TableCell className="text-sm text-zinc-400">{formatDate(p.due_date)}</TableCell>
                <TableCell><StatusBadge status={p.status} statusMap={PAYMENT_STATUSES} /></TableCell>
                <TableCell>
                  <span className={`text-xs ${p.followup_done ? 'text-emerald-400' : 'text-zinc-500'}`}>
                    {p.followup_done ? 'Fait' : 'Non'}
                  </span>
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-1">
                    <button data-testid={`edit-payment-${p.id}`} onClick={() => handleEdit(p)} className="p-1.5 text-zinc-500 hover:text-zinc-100 transition-colors">
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button data-testid={`delete-payment-${p.id}`} onClick={() => handleDelete(p.id)} className="p-1.5 text-zinc-500 hover:text-rose-400 transition-colors">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100 max-w-xl" data-testid="payment-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
              {editingPayment ? 'Modifier le paiement' : 'Nouveau paiement'}
            </DialogTitle>
            <DialogDescription className="text-zinc-500 text-sm">
              {editingPayment ? 'Modifiez les informations du paiement.' : 'Enregistrez un nouveau paiement.'}
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-4">
            <div className="space-y-2">
              <Label className="text-zinc-400 text-sm">Client *</Label>
              <Input data-testid="form-payment-client" value={form.client_name} onChange={(e) => setForm(f => ({ ...f, client_name: e.target.value }))}
                className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm" />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-400 text-sm">Projet</Label>
              <Input value={form.project_name} onChange={(e) => setForm(f => ({ ...f, project_name: e.target.value }))}
                className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm" />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-400 text-sm">Ref. facture</Label>
              <Input value={form.invoice_ref} onChange={(e) => setForm(f => ({ ...f, invoice_ref: e.target.value }))}
                className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm" />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-400 text-sm">Mode de paiement</Label>
              <Input value={form.payment_method} onChange={(e) => setForm(f => ({ ...f, payment_method: e.target.value }))}
                placeholder="Virement, cheque..." className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm" />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-400 text-sm">Montant HT <span className="text-zinc-600 text-xs">(TVA 20% auto)</span></Label>
              <Input data-testid="form-payment-ht" type="number" step="0.01" value={form.amount_ht}
                onChange={(e) => {
                  const ht = parseFloat(e.target.value) || 0;
                  setForm(f => ({ ...f, amount_ht: ht, amount_ttc: Math.round(ht * 1.20 * 100) / 100 }));
                }}
                className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm" />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-400 text-sm">Montant TTC <span className="text-zinc-600 text-xs">(TVA 20% auto)</span></Label>
              <Input data-testid="form-payment-ttc" type="number" step="0.01" value={form.amount_ttc}
                onChange={(e) => {
                  const ttc = parseFloat(e.target.value) || 0;
                  setForm(f => ({ ...f, amount_ttc: ttc, amount_ht: Math.round(ttc / 1.20 * 100) / 100 }));
                }}
                className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm" />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-400 text-sm">Date echeance</Label>
              <Input type="date" value={form.due_date} onChange={(e) => setForm(f => ({ ...f, due_date: e.target.value }))}
                className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm" />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-400 text-sm">Statut</Label>
              <Select value={form.status} onValueChange={(v) => setForm(f => ({ ...f, status: v }))}>
                <SelectTrigger className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  {Object.entries(PAYMENT_STATUSES).map(([k, v]) => (<SelectItem key={k} value={k}>{v.label}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2 flex items-center gap-2">
              <Checkbox id="followup" checked={form.followup_done} onCheckedChange={(v) => setForm(f => ({ ...f, followup_done: !!v }))} />
              <Label htmlFor="followup" className="text-zinc-400 text-sm">Relance effectuee</Label>
            </div>
            <div className="col-span-2 space-y-2">
              <Label className="text-zinc-400 text-sm">Commentaire</Label>
              <Textarea value={form.comments} onChange={(e) => setForm(f => ({ ...f, comments: e.target.value }))}
                className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 rounded-sm">Annuler</Button>
            <Button data-testid="save-payment-btn" onClick={handleSave} className="bg-amber-500 text-zinc-950 hover:bg-amber-400 rounded-sm font-medium">
              {editingPayment ? 'Mettre a jour' : 'Creer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
